import { useEffect, useState } from "react";

import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Tooltip as LTooltip,
} from "react-leaflet";

import { useDispatch } from "react-redux";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import "./FieldMonitoring.css";
import { getFieldAgentMonitoring } from "../Store/Actions/MonitoringAction";
import { Dialog, IconButton, Tooltip } from "@mui/material";
import { SET_AGENT_SELECTED_ASSIGNMENT } from "../Store/Types/AgentTypes";

const FieldMonitoring = ({ changePage, isOverviewShown }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);

  useEffect(() => {
    const fetchData = () => {
      getFieldAgentMonitoring()
        .then((response) => {
          if (response.status) {
            console.log("Monitoring data: ", response.data);
            setData(response.data);
          } else {
            console.log("error", response.error);
          }

          setIsLoading(false);
        })
        .catch((err) => {
          console.log("err", err);
          setIsLoading(false);
        });
    };

    // Fetch data on first render
    fetchData();

    // Fetch data every one minute
    const interval = setInterval(() => {
      fetchData();
    }, 1000 * 60);

    return () => clearInterval(interval);
  }, []);

  const dispatch = useDispatch();

  const columns = [
    {
      field: "AgentCode",
      flex: 0.25,
      headerName: "Agent",
    },

    {
      cellClassName: "current-assignment",
      field: "CurrentAssignment",
      flex: 1,
      headerName: "Current Assignment",

      renderCell: (params) =>
        params.row.CurrentAssignment ? (
          <div>
            <p>{params.row.CurrentAssignment}</p>

            <p className="campaign-name">
              Campaign: {params.row.CampaignName ?? "not selected"}
            </p>
          </div>
        ) : (
          "No assignment selected"
        ),
    },

    {
      field: "Today",
      flex: 0.4,
      headerName: "Today",

      renderCell: (params) => {
        if (params.row.Today) {
          const values = params.row.Today.split("/").map((part) => part.trim());
          return `${values[0]} Visited / ${values[1]} Completed`;
        } else {
          return "N / A";
        }
      },
    },

    {
      field: "Progress",
      flex: 0.7,
      headerName: "Progress",

      renderCell: (params) => {
        if (params.row.Progress) {
          const values = params.row.Progress.split("/").map((part) =>
            part.trim()
          );

          return `${values[0]} Visited / ${values[1]} Completed / ${values[2]} Assigned`;
        } else {
          return "N / A";
        }
      },
    },

    {
      field: "Geocode",
      flex: 0.5,
      headerName: "Location",
      renderCell: (params) => params.row.Geocode ?? "Location not found",
    },

    {
      field: "UpdatedTime",
      flex: 0.3,
      headerName: "Updated",
      renderCell: (params) => moment(params.row.UpdatedTime).from(moment()),
    },

    {
      flex: 0.25,
      headerName: "Action",

      renderCell: (params) => {
        return (
          <>
            <Tooltip arrow placement="top" title="View location">
              <IconButton
                onClick={() => {
                  setSelectedCoordinates({
                    latitude: params.row.Latitude,
                    longitude: params.row.Longitude,
                  });

                  setIsMapOpen(true);
                }}
              >
                <Icon height="26" icon="material-symbols:my-location" />
              </IconButton>
            </Tooltip>

            <Tooltip arrow placement="top" title="View assignment">
              <IconButton
                disabled={!params.row.CurrentAssignmentID}
                onClick={() => {
                  dispatch({
                    type: SET_AGENT_SELECTED_ASSIGNMENT,
                    payload: params.row.CurrentAssignmentID,
                  });

                  changePage("campaign");
                }}
              >
                <Icon height="26" icon="ic:round-assignment" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  // Leaflet marker icon
  let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
  });

  L.Marker.prototype.options.icon = DefaultIcon;

  function handleMapClose() {
    setIsMapOpen(false);
    setSelectedCoordinates(null);
  }

  return (
    <>
      {isOverviewShown ? (
        <MapContainer
          center={[-6.2337, 106.8352]}
          className="overview-map"
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%", zIndex: 1 }}
          zoom={12}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {data.map((data) => (
            <Marker
              key={data.AgentID}
              position={[data.Latitude ?? 0, data.Longitude ?? 0]}
            >
              <LTooltip
                direction="top"
                offset={[12.5, 0]}
                opacity={1}
                permanent
              >
                {data.AgentCode}
              </LTooltip>

              <Popup offset={[12.5, 0]}>
                <p>
                  {data.Geocode} - {moment(data.UpdatedTime).from(moment())}
                </p>

                <p>{data.CurrentAssignment}</p>
                <p>{data.CampaignName}</p>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      ) : (
        <DataGrid
          autoPageSize
          className="agent-monitoring table"
          columns={columns}
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          loading={isLoading}
          rows={data}
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 1.8,
            },
          }}
          getRowClassName={() => "row"}
          getRowId={(row) => row.AgentID}
        />
      )}

      <Dialog
        open={isMapOpen}
        className="dialog field-map-dialog"
        fullWidth
        scroll="paper"
        onClose={handleMapClose}
      >
        <div className="dialog-container">
          <div className="dialog-content">
            <IconButton className="close-button" onClick={handleMapClose}>
              <CloseOutlined />
            </IconButton>

            <iframe
              height="500"
              loading="lazy"
              src={`https://www.google.com/maps/embed/v1/place?q=${selectedCoordinates?.latitude}, ${selectedCoordinates?.longitude}&key=AIzaSyCaWAR4c26PRYTXqycgYSkNR89-nIDPMI0&zoom=12`}
              title="maps"
              width="500"
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default FieldMonitoring;
